import React, { useCallback, useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { FaEdit, FaTrash, FaSave, FaTimes } from "react-icons/fa";
import { apiCall } from "../main/Apicall";
import Pagination from "../Reusablecomponents/Pagination";
import ToastPopup from "../Popups/ToastPopup";
import "../css/others/Table.css";
import DeleteFunctionChecklistQuestion from "../Popups/DeleteFunctionChecklistQuestion";
import EditFunctionChecklistQuestion from "../Popups/EditFunctionChecklistQuestion";
import { Question, createToastMessage, ToastMessage } from "../Interfaces/Interface";
const FunctionChecklistDetails: React.FC = () => {
  const { functionTemplateId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { status } = location.state || { status: "DRAFT" };
  const [details, setDetails] = useState<any>({ content: [] });
  const [, setLoading] = useState<boolean>(true);
  const [newQuestions, setNewQuestions] = useState<Partial<Question>[]>([]);
  const [isEditModalOpen, setIsEditModalOpen] = useState<boolean>(false);
  const [editableQuestion, setEditableQuestion] = useState<Partial<Question> | null>(null);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage, setItemsPerPage] = useState<number>(10);
  const [totalElements, setTotalElements] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(0);
  const [message, setMessage] = useState<ToastMessage | null>(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null);
  const [showRedirectPopup, setShowRedirectPopup] = useState<boolean>(false);
  useEffect(() => {
    if (status === "ACTIVE") {
      const timer = setTimeout(() => {
        setShowRedirectPopup(true);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [status]);
  useEffect(() => {
    if (showRedirectPopup) {
      const timer = setTimeout(() => {
        navigate(-1);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showRedirectPopup, navigate]);
  const fetchChecklistDetails = useCallback(async () => {
    if (!functionTemplateId) return;
    setLoading(true);
    try {
      const endpoint = status === "ACTIVE" ? `/api/function/templates/${functionTemplateId}/questions/edit` : `/api/function/templates/${functionTemplateId}/questions/edit`;
      const checklist = await apiCall("get", endpoint, undefined, { page: currentPage - 1, size: itemsPerPage });
      setDetails(checklist || { content: [] });
      setTotalElements(checklist.totalElements || 0);
      setTotalPages(checklist.totalPages || 0);
    } catch (error) {
      console.error("Error fetching checklist details:", error);
    } finally {
      setLoading(false);
    }
  }, [functionTemplateId, currentPage, itemsPerPage, status]);
  useEffect(() => { fetchChecklistDetails(); }, [fetchChecklistDetails]);
  const handleAddRow = () => {
    const newRow: Partial<Question> = {};
    setNewQuestions((prev) => [...prev, newRow]);
  };
  const handleRemoveRow = (id: number) => {
    setSelectedQuestionId(id);
    setIsDeleteModalOpen(true);
  };
  const confirmDelete = async () => {
    if (!functionTemplateId || !selectedQuestionId) return;
    try {
      await apiCall("delete", `/api/function/templates/${functionTemplateId}/questions/${selectedQuestionId}`);
      setMessage(createToastMessage("Question deleted successfully", "success", 3000));
      fetchChecklistDetails();
    } catch (error) {
      setMessage(createToastMessage("Error deleting question", "error", 3000));
    } finally {
      setIsDeleteModalOpen(false);
      setSelectedQuestionId(null);
    }
  };
  const handleSaveNewQuestions = async () => {
    if (newQuestions.length > 0 && functionTemplateId) {
      const questionsToAdd = newQuestions.map((question) => ({ functionAreaName: question.functionAreaName, questionText: question.questionText, evidenceRequired: question.evidenceRequired, isoClauseReference: question.isoClauseReference, annexAControlReference: question.annexAControlReference, createdBy: "admin@gmail.com", status: "ACTIVE" }));
      try {
        await apiCall("post", `/api/function/templates/${functionTemplateId}/questions`, questionsToAdd);
        setNewQuestions([]);
        setMessage(createToastMessage("Questions added successfully", "success", 3000));
        fetchChecklistDetails();
      } catch (error) {
        setMessage(createToastMessage("Error adding questions", "error", 3000));
      }
    }
  };
  const handleEditClick = (id: number) => {
    const questionToEdit = details?.content.find((item: { functionQuestionId: number; }) => item.functionQuestionId === id);
    if (questionToEdit) {
      setEditableQuestion(questionToEdit);
      setIsEditModalOpen(true);
    }
  };
  const handleModalChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setEditableQuestion((prev: any) => ({ ...prev, [name]: value }));
  };
  const handleSaveChanges = async () => {
    if (editableQuestion && functionTemplateId) {
      try {
        await apiCall("put", `/api/function/templates/${functionTemplateId}/questions`, [{ functionQuestionId: editableQuestion.functionQuestionId, functionAreaName: editableQuestion.functionAreaName, questionText: editableQuestion.questionText, evidenceRequired: editableQuestion.evidenceRequired, isoClauseReference: editableQuestion.isoClauseReference, annexAControlReference: editableQuestion.annexAControlReference }]);
        setMessage(createToastMessage("Question updated successfully", "success", 3000));
        fetchChecklistDetails();
        setIsEditModalOpen(false);
      } catch (error) {
        setMessage(createToastMessage("Error updating question", "error", 3000));
      }
    }
  };
  const handleCancelNewQuestions = () => { setNewQuestions([]); };
  return (
    <div className="table-details">
      <div className="table-title">
        <h2>Function Checklist Details</h2>
        <button className="add-question-button" onClick={handleAddRow}>Add Question</button>
      </div>
      <div className="table-container">
        <div className="table-inner-container">
          <table className="main-table">
            <thead>
              <tr>
                <th>S.No.</th>
                <th>Function Area Name</th>
                <th>Question</th>
                <th>Evidence Required</th>
                <th>ISO Clause Reference</th>
                <th>Annex A Control Reference</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {details?.content?.length > 0 || newQuestions.length > 0 ? (
                <>
                  {details?.content?.map((item: any, index: number) => (
                    <tr key={item.functionQuestionId}>
                      <td>{(currentPage - 1) * itemsPerPage + index + 1}</td>
                      <td>{item.functionAreaName || "Empty"}</td>
                      <td>{item.questionText || "Empty"}</td>
                      <td>{item.evidenceRequired || "Empty"}</td>
                      <td>{item.isoClauseReference || "Empty"}</td>
                      <td>{item.annexAControlReference || "Empty"}</td>
                      <td>
                        <FaEdit className="action-icon" onClick={() => handleEditClick(item.functionQuestionId)} title="Edit" />
                        <FaTrash className="action-icon delete-icon" onClick={() => handleRemoveRow(item.functionQuestionId)} title="Delete" />
                      </td>
                    </tr>
                  ))}
                  {newQuestions.map((question, index) => (
                    <tr key={`new-${index}`}>
                      <td>{totalElements + index + 1}</td>
                      <td>
                        <textarea value={question.functionAreaName || ""} onChange={(e) => { const updatedQuestions = [...newQuestions]; updatedQuestions[index].functionAreaName = e.target.value; setNewQuestions(updatedQuestions) }} placeholder="Enter Function Area Name" />
                      </td>
                      <td>
                        <textarea value={question.questionText || ""} onChange={(e) => { const updatedQuestions = [...newQuestions]; updatedQuestions[index].questionText = e.target.value; setNewQuestions(updatedQuestions) }} placeholder="Enter question text" />
                      </td>
                      <td>
                        <textarea value={question.evidenceRequired || ""} onChange={(e) => { const updatedQuestions = [...newQuestions]; updatedQuestions[index].evidenceRequired = e.target.value; setNewQuestions(updatedQuestions) }} placeholder="Enter evidence required" />
                      </td>
                      <td>
                        <textarea value={question.isoClauseReference || ""} onChange={(e) => { const updatedQuestions = [...newQuestions]; updatedQuestions[index].isoClauseReference = e.target.value; setNewQuestions(updatedQuestions) }} placeholder="Enter ISO clause reference" />
                      </td>
                      <td>
                        <textarea value={question.annexAControlReference || ""} onChange={(e) => { const updatedQuestions = [...newQuestions]; updatedQuestions[index].annexAControlReference = e.target.value; setNewQuestions(updatedQuestions) }} placeholder="Enter Annex A control reference" />
                      </td>
                      <td>
                        <FaSave className="action-icon" onClick={handleSaveNewQuestions} title="Save" />
                        <FaTimes className="action-icon" onClick={handleCancelNewQuestions} title="Cancel" />
                      </td>
                    </tr>
                  ))}
                </>
              ) : (
                <tr>
                  <td colSpan={7} className="no-data">No questions available for this checklist</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        totalElements={totalElements}
        itemsPerPage={itemsPerPage}
        onPageChange={setCurrentPage}
        onItemsPerPageChange={setItemsPerPage}
      />
      {isEditModalOpen && editableQuestion && (
        <EditFunctionChecklistQuestion
          question={editableQuestion}
          onChange={handleModalChange}
          onClose={() => setIsEditModalOpen(false)}
          onSave={handleSaveChanges}
        />
      )}
      {isDeleteModalOpen && (
        <DeleteFunctionChecklistQuestion
          onCancel={() => setIsDeleteModalOpen(false)}
          onConfirm={confirmDelete}
        />
      )}
      {showRedirectPopup && (
        <div className="modal-overlay">
          <div className="modal-content">
            <h3>Redirection Notice</h3>
            <p>This checklist is in ACTIVE status. Redirecting to draft version...</p>
          </div>
        </div>
      )}
      {message && (<ToastPopup message={message.text} type={message.type} duration={message.duration} onClose={() => setMessage(null)} />)}
    </div>
  );
};
export default FunctionChecklistDetails;